import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X } from 'lucide-react';

const bonusLabel = { fr: 'Un petit truc en plus', ru: 'Маленький бонус', ko: '작은 보너스', zh: '小彩蛋' };

const Navbar = () => {
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const languages = [
    { code: 'fr', name: 'FR', flag: '🇫🇷' },
    { code: 'ru', name: 'RU', flag: '🇷🇺' },
    { code: 'ko', name: 'KO', flag: '🇰🇵' },
    { code: 'zh', name: 'ZH', flag: '🇨🇳' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" onClick={() => setMobileMenuOpen(false)}>
            <div className="text-2xl md:text-3xl font-light text-[#d4c5a9] group-hover:text-[#e5d7bb] transition-colors">
              &
            </div>
            <div className="text-base md:text-xl font-light tracking-wide text-white truncate max-w-[120px] md:max-w-none">
              GlobalIntel<span className="text-[#d4c5a9]">&</span>Co
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm tracking-wide transition-colors ${
                isActive('/') ? 'text-[#d4c5a9]' : 'text-gray-300 hover:text-white'
              }`}
            >
              {t.nav.home}
            </Link>
            <Link
              to="/services"
              className={`text-sm tracking-wide transition-colors ${
                isActive('/services') ? 'text-[#d4c5a9]' : 'text-gray-300 hover:text-white'
              }`}
            >
              {t.nav.services}
            </Link>
            <Link
              to="/zones"
              className={`text-sm tracking-wide transition-colors ${
                isActive('/zones') ? 'text-[#d4c5a9]' : 'text-gray-300 hover:text-white'
              }`}
            >
              {t.nav.zones}
            </Link>
            <Link
              to="/management"
              className={`text-sm tracking-wide transition-colors ${
                isActive('/management') ? 'text-[#d4c5a9]' : 'text-gray-300 hover:text-white'
              }`}
            >
              {t.nav.management}
            </Link>
            <Link
              to="/press"
              className={`text-sm tracking-wide transition-colors ${
                isActive('/press') ? 'text-[#d4c5a9]' : 'text-gray-300 hover:text-white'
              }`}
            >
              {t.nav.press}
            </Link>
            <Link
              to="/bonus"
              className={`text-sm tracking-wide transition-colors ${
                isActive('/bonus') ? 'text-[#d4c5a9]' : 'text-gray-300 hover:text-white'
              }`}
            >
              {bonusLabel[language]}
            </Link>

            {/* POC/DEMO Badge */}
            <div className="px-3 py-1 bg-gradient-to-r from-red-600/20 to-orange-600/20 border border-red-500/30 rounded text-xs font-medium text-red-400 tracking-wider">
              {t.nav.demo}
            </div>
          </div>

          {/* Mobile + Language Container */}
          <div className="flex items-center gap-2">
            {/* Language Selector - VISIBLE WITH FLAGS */}
            <div className="flex items-center gap-1 md:gap-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`flex flex-col items-center justify-center px-2 py-1.5 md:px-3 md:py-2 rounded-lg transition-all duration-300 ${
                    language === lang.code
                      ? 'bg-[#d4c5a9] text-black scale-110'
                      : 'bg-gray-800/50 text-gray-300 hover:bg-gray-800 hover:scale-105'
                  }`}
                  title={lang.code === 'fr' ? 'Français' : lang.code === 'ru' ? 'Русский' : lang.code === 'ko' ? '한국어' : '中文'}
                >
                  <span className="text-lg md:text-2xl mb-0.5 md:mb-1">{lang.flag}</span>
                  <span className="text-[10px] md:text-xs font-bold">{lang.name}</span>
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-300 hover:text-white"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-black/95 backdrop-blur-lg lg:hidden pt-20">
          <div className="flex flex-col items-center justify-center h-full gap-8 px-6">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`text-2xl tracking-wide transition-colors ${
                isActive('/') ? 'text-[#d4c5a9]' : 'text-gray-300'
              }`}
            >
              {t.nav.home}
            </Link>
            <Link
              to="/services"
              onClick={() => setMobileMenuOpen(false)}
              className={`text-2xl tracking-wide transition-colors ${
                isActive('/services') ? 'text-[#d4c5a9]' : 'text-gray-300'
              }`}
            >
              {t.nav.services}
            </Link>
            <Link
              to="/zones"
              onClick={() => setMobileMenuOpen(false)}
              className={`text-2xl tracking-wide transition-colors ${
                isActive('/zones') ? 'text-[#d4c5a9]' : 'text-gray-300'
              }`}
            >
              {t.nav.zones}
            </Link>
            <Link
              to="/management"
              onClick={() => setMobileMenuOpen(false)}
              className={`text-2xl tracking-wide transition-colors ${
                isActive('/management') ? 'text-[#d4c5a9]' : 'text-gray-300'
              }`}
            >
              {t.nav.management}
            </Link>
            <Link
              to="/press"
              onClick={() => setMobileMenuOpen(false)}
              className={`text-2xl tracking-wide transition-colors ${
                isActive('/press') ? 'text-[#d4c5a9]' : 'text-gray-300'
              }`}
            >
              {t.nav.press}
            </Link>
            <Link
              to="/bonus"
              onClick={() => setMobileMenuOpen(false)}
              className={`text-2xl tracking-wide transition-colors ${
                isActive('/bonus') ? 'text-[#d4c5a9]' : 'text-gray-300'
              }`}
            >
              {bonusLabel[language]}
            </Link>

            <div className="px-4 py-2 bg-gradient-to-r from-red-600/20 to-orange-600/20 border border-red-500/30 rounded text-sm font-medium text-red-400 tracking-wider">
              {t.nav.demo}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;